import { memo, useRef, useState, useEffect, useSyncExternalStore } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { useSegmentTransition } from '../../hooks/useSegmentTransition';
import AddSegmentMenuOptions from './AddSegmentMenuOptions';
import AddSegmentUploadView from './AddSegmentUploadView';
import { useAddSegmentUpload } from './useAddSegmentUpload';
import type { AddMenuView, AddSegmentButtonProps } from './types';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';
import featureFlagStore from '@/stores/featureFlagStore';
import IntroOutroModal from '@/Pages/BrandKit/components/IntroOutro/IntroOutroModal';
import useDialog from '@/components/organisms/useDialog';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { startAddClipToTimeline } from '@/stores/addClipToTimelineStore';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useAnalytics from '@/hooks/useAnalytics';
import useSavedSearch from '@/hooks/useSavedSearch';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';

function AddSegmentButton({
  previousSegment,
  nextSegment = null,
  isTransitionApplied = false,
  showUploadOption = true,
  showAddClipOption = true
}: AddSegmentButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AddMenuView>('menu');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isSegmentTransitionsEnabled = featureFlags[FeatureFlagKeys.Use_CL_Segment_Transitions];
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const isAddClipEnabled = !!featureFlags[FeatureFlagKeys.Use_CL_Add_Clip_To_Timeline];

  // The picker lists only clips from the same source, so on an AI search — whose clips span
  // multiple sources — it would offer a near-empty list. Hide the option there entirely.
  const { isSavedSearchPage } = useSavedSearch();

  const { isOpen: isModalOpen, openDialog: openModal, closeDialog: closeModal } = useDialog();
  const { clipData } = useClipsContext();
  const { isBlocked } = useEditorRestrictions();

  const { trackAddClipTapped } = useAnalytics();
  const upload = useAddSegmentUpload(previousSegment);
  const { toggleTransition, canToggle: canToggleTransition } = useSegmentTransition({
    previousSegment,
    nextSegment,
    isTransitionApplied
  });

  const showMenu = isSegmentTransitionsEnabled;

  const updatePopoverPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePopoverPosition();
      const handleScroll = () => updatePopoverPosition();
      const handleResize = () => updatePopoverPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  // Every route out of the + mutates the clip, so with all of them blocked it opens an inert menu.
  const canAddSegment = showUploadOption && !isBlocked('timeline-intro-outro-edit');
  const canEditTransition = showMenu && !isBlocked('timeline-transition-edit');
  if (!canAddSegment && !canEditTransition) {
    return null;
  }

  const handleClose = (close: () => void) => () => {
    close();
    setIsOpen(false);
    setView('menu');
  };

  const handleButtonClick = () => {
    if (usePlatformBrandKit && !showMenu) {
      openModal();
      return;
    }
    setView(showMenu ? 'menu' : 'upload');
    setIsOpen(true);
    updatePopoverPosition();
  };

  const handleUploadFileClick = (close: () => void) => {
    if (usePlatformBrandKit) {
      handleClose(close)();
      openModal();
    } else {
      setView('upload');
    }
  };

  // Add Clip hands off to the clips sidebar rather than showing its own list, so the picker is
  // the same panel users already know. The gap's insert handler is parked in a store because the
  // sidebar is mounted far from the timeline and cannot receive it as a prop.
  const handleAddClipClick = (close: () => void) => {
    handleClose(close)();
    trackAddClipTapped({ sourceClipId: clipData.id });
    startAddClipToTimeline({
      onInsert: upload.handleClipSegmentInsert,
      targetLayout: clipData.asset_metadata.layout,
      sourceClipId: clipData.id
    });
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: true });
  };

  const tooltipId = `add-segment-button-${previousSegment?.id ?? 'start'}-${nextSegment?.id ?? 'end'}`;

  return (
    <>
      <Popover>
        {({ close }) => (
          <>
            <TooltipV2
              tooltipId={tooltipId}
              tooltipContent="Add..."
              noArrow
              place="top"
              className="z-50"
              offset={25}
              tooltipElement={
                <Popover.Button
                  ref={buttonRef}
                  onClick={handleButtonClick}
                  className="absolute left-1/2 top-1/2 z-50 flex size-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-200 opacity-0 transition-opacity duration-200 hover:border-slate-400 hover:bg-slate-300 group-hover:opacity-100"
                >
                  <Icon name="IconPlus" width={16} height={16} className="text-slate-600" />
                </Popover.Button>
              }
            />
            <Transition
              appear
              show={isOpen}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
              afterLeave={() => setIsOpen(false)}
            >
              <Popover.Panel
                static
                className={`fixed z-[60] rounded-xl border bg-white shadow-lg ${view === 'menu' ? 'w-48' : 'w-[382px]'}`}
                style={{
                  top: `${popoverPosition.top}px`,
                  left: `${popoverPosition.left}px`,
                  transform: 'translate(-50%, -100%)'
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
              >
                <div className={`max-h-[500px] overflow-y-auto ${view === 'menu' ? 'p-2' : 'px-4 py-3'}`}>
                  {view === 'menu' && (
                    <AddSegmentMenuOptions
                      onUploadFileClick={() => handleUploadFileClick(close)}
                      onTransitionClick={() => toggleTransition(handleClose(close))}
                      onAddClipClick={() => handleAddClipClick(close)}
                      isTransitionApplied={isTransitionApplied}
                      canToggleTransition={canToggleTransition}
                      showUploadOption={showUploadOption}
                      showAddClipOption={isAddClipEnabled && showAddClipOption && !isSavedSearchPage}
                    />
                  )}
                  {view === 'upload' && !usePlatformBrandKit && (
                    <AddSegmentUploadView
                      onClose={handleClose(close)}
                      clipIntroOutro={upload.clipIntroOutro}
                      brandKitIntroOutro={upload.brandKitIntroOutro}
                      brandKitStore={upload.brandKitStore}
                      uploadFiles={upload.uploadFiles}
                      onFilesUpload={upload.onFilesUpload}
                      handleVideoSelect={upload.handleVideoSelect}
                      deleteItem={upload.deleteItem}
                      removedBrandKitName={upload.removedBrandKitName}
                    />
                  )}
                </div>
              </Popover.Panel>
            </Transition>
            {isOpen && (
              <div
                className="fixed inset-0 z-50"
                onClick={() => {
                  setView('menu');
                  setIsOpen(false);
                  close();
                }}
              />
            )}
          </>
        )}
      </Popover>
      {usePlatformBrandKit && (
        <IntroOutroModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onVideoSelect={upload.handleVideoSelect}
          title="Add Video"
        />
      )}
    </>
  );
}

export default memo(AddSegmentButton);
